/**
 * @author lifei
 * 功能：缓存控制，不考虑用户禁用本地存储的情况，未做拦截，后面会优化处理
 * update: 2024-10-14 更新部分方法的访问权限和部分逻辑调整
 */

import { snakeCase } from 'lodash-es'
import { Base } from '../../base'

type TType = 'local' | 'session'
type TOptions = { expired: Date }

type TData = {
  value: any
  expired?: number
}

type TKeysItem = {
  key: string
  type: TType
}

export class Memory extends Base {
  constructor(key?: string, type?: TType) {
    super()
    if (key && type) {
      this.key = this.formatKey(key)
      this.type = type
      this.addKeys(type, key)
    }
  }

  /**
   * 功能：存储所有的键值, 后期会单独创建 队列实体类，用于统一处理队列逻辑
   */
  private queue = new Map<TType, Array<string>>()

  /**
   * 功能：存储数据的 key
   */
  private key: string = ''

  /**
   * 功能：存储类型
   */
  private type: TType = 'local'

  /**
   * 功能：静态化实例
   */
  private static readonly instance = new Memory()

  /**
   * 功能：类型映射
   */
  private readonly map = new Map<TType, Storage>([
    ['local', window.localStorage],
    ['session', window.sessionStorage],
  ])

  /**
   * 功能：将 key 转成大写，并拼接应用前缀如：XH_TOKEN
   * @param key：存储的键
   * @returns
   */
  private formatKey = (key: string): string => [this.prefix, snakeCase(key)].map((item) => item.toUpperCase()).join('_')

  /**
   * 功能：保存键值到队列
   */
  private addKeys = (type: TType, key: string) => {
    let keys = this.queue.get(type) || []
    if (keys.includes(key)) {
      throw new Error(`The ${key} already exists`)
    }
    this.queue.set(type, [...keys, key])
  }

  /**
   * 功能：删除队列键值
   */
  private removeKeys = (type: TType, key: string) => {
    let keys = this.queue.get(type) || []
    this.queue.set(
      type,
      keys.filter((item) => item != key)
    )
  }

  /**
   * 功能：存储数据
   */
  public setItem = <T>(data: T, options?: TOptions): void => {
    const ret: TData = {
      value: data,
    }

    if (options?.expired) {
      const { expired } = options
      const timestamp = expired.getTime()
      if (Date.now() > timestamp) {
        console.error(`The timestamp is invalid`)
      } else {
        ret.expired = timestamp
      }
    }
    this.map.get(this.type)?.setItem(this.key, JSON.stringify(ret))
  }

  /**
   * 功能：获取存储数据
   */
  public getItem = <T = any>(): TCommon.OrNull<T> => {
    const store = this.map.get(this.type)

    if (!store) return null

    const { value, expired } = JSON.parse(store.getItem(this.key) || '{"value": null}')

    if (!expired || expired > Date.now()) return value

    store.removeItem(this.key)

    return null
  }

  /**
   * 功能：清空存储
   */
  public static clear(type: TType): void {
    this.instance.map.get(type)?.clear()
  }

  /**
   * 功能：清空全部存储
   */
  public static clearAll = (): void => {
    ;[...this.instance.map].forEach(([key, value]) => {
      value.clear()
    })
    this.instance.queue.clear()
  }

  /**
   * 功能：获取存储的 key
   */
  protected static getKeys = (type: TType): Array<string> => this.instance.queue.get(type) || []

  /**
   * 功能：获取全部的存储的 key
   */
  public static getAllKeys = (): Array<TKeysItem> => {
    let ret: Array<TKeysItem> = []
    const types: TType[] = ['local', 'session']
    types.forEach((type) => {
      this.getKeys(type)?.forEach((key) => {
        ret.push({ key, type })
      })
    })
    return ret
  }

  /**
   * 功能：移除存储
   */
  public removeItem = (): void => {
    const store = this.map.get(this.type)
    if (store) {
      store.removeItem(this.key)
      this.removeKeys(this.type, this.key)
    }
  }

  /**
   * 功能：删除一组存储数据
   */
  public static removeItems = (type: TType, keys: Array<string>): void => {
    const list = this.instance.queue.get(type)
    keys
      .map((item) => this.instance.formatKey(item))
      .forEach((item) => {
        list?.includes(item) && this.instance.map.get(type)?.removeItem(item)
      })
  }
}
